import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import React from 'react'
import DayScheduleFooter from './DayScheduleFooter'
import DayScheduleHeader from './DayScheduleHeader'
import DayScheduleContent from './DayScheduleContent'
import { axiosInstance } from '@/lib/axios'
import { ScheduleDayModel } from 'app/models/timesheet/scheduleDayModel'
import { toast } from '@/lib/toasts/api'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useInvalidateDriverRequiredActions } from '@/lib/api/company/drivers/useQueryDriverRequiredActions'

type DayScheduleModalProps = {
  refreshSchedule: () => void
  companyId: string
  close: () => void
  driverId: number
  selectedDays: ScheduleDayModel[]
}

const DayScheduleModal = ({
  refreshSchedule,
  companyId,
  close,
  driverId,
  selectedDays,
}: DayScheduleModalProps) => {
  const [loading, setLoading] = React.useState(false)
  const { tSafe } = useLocalize()
  const invalidateDriverRequiredActions = useInvalidateDriverRequiredActions()

  const initialData = React.useMemo(() => {
    const empty = {
      dayScheduleTime: '',
      scheduleTime: '' as number | string,
      showSchedule: false,
    }
    if (!selectedDays || selectedDays.length === 0) return empty

    const startTimes = selectedDays.map((d) =>
      d.dayStartTime ? d.dayStartTime.format('HH:mm') : '',
    )
    const scheduleTimes = selectedDays.map((d) => {
      if (d.dayScheduleTime == null || d.dayScheduleTime === '') return ''
      const raw = String(d.dayScheduleTime)
      const [hh = '0', mm = '0'] = raw.split(':')
      // TimeSpan >= 24 h przychodzi jako "d.hh:mm:ss".
      const [days, hoursPart] = hh.includes('.') ? hh.split('.') : ['0', hh]
      const hours =
        parseInt(days, 10) * 24 + parseInt(hoursPart, 10) + parseInt(mm, 10) / 60
      if (!isFinite(hours)) return ''
      return Number.isInteger(hours) ? hours : Number(hours.toFixed(2))
    })

    const allSame = <T,>(arr: T[]) => arr.every((v) => v === arr[0])

    return {
      dayScheduleTime: allSame(startTimes) ? startTimes[0] : '',
      scheduleTime: allSame(scheduleTimes) ? scheduleTimes[0] : '',
      showSchedule: allSame(selectedDays.map((d) => d.showSchedule))
        ? selectedDays[0].showSchedule
        : false,
    }
  }, [selectedDays])

  const [data, setData] = React.useState<{
    dayScheduleTime: string
    scheduleTime: number | string
    showSchedule: boolean
  }>(initialData)

  const toTimeString = (value: number | string): string => {
    const num = typeof value === 'number' ? value : parseFloat(value)
    if (!isFinite(num) || num < 0) return '00:00:00'
    const totalMinutes = Math.round(num * 60)
    const totalHours = Math.floor(totalMinutes / 60)
    // .NET czyta "24:00:00" jako 24 dni, więc pełne doby idą w formacie "d.hh:mm:ss".
    const days = Math.floor(totalHours / 24)
    const hh = (totalHours % 24).toString().padStart(2, '0')
    const mm = (totalMinutes % 60).toString().padStart(2, '0')
    return days > 0 ? `${days}.${hh}:${mm}:00` : `${hh}:${mm}:00`
  }

  const normalizeTime = (value: string): string => {
    if (!value) return '00:00:00'
    const parts = value.split(':')
    const hh = (parts[0] ?? '00').padStart(2, '0')
    const mm = (parts[1] ?? '00').padStart(2, '0')
    const ss = (parts[2] ?? '00').padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }

  const changeSchedule = async () => {
    if (!selectedDays || selectedDays.length === 0) {
      close()
      return
    }
    setLoading(true)
    try {
      const monthFirstDay = selectedDays[0].dayDate
        .clone()
        .startOf('month')
        .format('YYYY-MM-DDTHH:mm:ss')

      const startTimeOfDay = normalizeTime(data.dayScheduleTime)
      const scheduleTimeStr = toTimeString(data.scheduleTime)

      const days = selectedDays.map((day) => ({
        dayDate: day.dayDate.format('YYYY-MM-DDTHH:mm:ss'),
        dayStartTime: `${day.dayDate.format('YYYY-MM-DD')}T${startTimeOfDay}`,
        dayScheduleTime: scheduleTimeStr,
        showSchedule: data.showSchedule,
      }))

      const response = await axiosInstance.post(
        `/api/${companyId}/timesheet/schedule`,
        {
          driverId,
          monthFirstDay,
          days,
        },
      )

      // Grafik wchodzi do wyliczen ewidencji, wiec gdy miesiac byl juz przeliczony,
      // backend oznacza go jako nieaktualny i oddaje to ostrzezenie - inaczej zmiana
      // grafiku po cichu rozjezdza sie z kwotami na ewidencji.
      const warnings = Array.isArray(response?.data) ? response.data : []
      if (warnings.some((item: any) => item?.code === 'TIMESHEET_EXISTS_FOR_DATE')) {
        toast.warning({ text: tSafe('errorCode.TIMESHEET_EXISTS_FOR_DATE') })
      }

      // Zapis harmonogramu uniewaznia przeliczony miesiac - znacznik przy kierowcy ma to
      // pokazac od razu, a nie po odswiezeniu przegladarki.
      invalidateDriverRequiredActions()
      refreshSchedule()
      close()
    } catch (error: any) {
      console.error('Error changing day schedule:', error)
      const status = error?.response?.status
      const code = error?.response?.data?.code ?? error?.response?.data?.Code
      const text =
        status === 401 || status === 403
          ? tSafe('errorCode.NOT_AUTHORIZED', 'Brak uprawnień do tej operacji.')
          : code === 'SCHEDULE_DAY_OUT_OF_MONTH'
            ? tSafe(
                'errorCode.SCHEDULE_DAY_OUT_OF_MONTH',
                'Zaznaczone dni muszą należeć do jednego miesiąca.',
              )
            : tSafe(
                'errorCode.SCHEDULE_SAVE_FAILED',
                'Nie udało się zapisać harmonogramu.',
              )
      toast.error({ text })
    } finally {
      setLoading(false)
    }
  }
  return (
    <ModalLayout customZIndex={9999}>
      <div className="tw-flex tw-h-fit tw-w-full tw-max-w-[350px] tw-flex-col tw-justify-between tw-rounded-lg tw-bg-white">
        <DayScheduleHeader close={close} />
        <DayScheduleContent data={data} setData={setData} />
        <DayScheduleFooter
          loading={loading}
          submitData={() => changeSchedule()}
        />
      </div>
    </ModalLayout>
  )
}

export default DayScheduleModal
